import { createElement, useState } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';

import { ChatSearchResultChips } from '@/components/chat/ChatSearchResultChips.web';
import { SidebarChevronIcon } from '@/components/layout/SidebarIcons.web';
import { Text } from '@/components/ui';
import { parseSearchToolResultChips } from '@/lib/chat/search-tool-results';
import type { LiveToolActivity, Message } from '@/lib/chat/types';
import {
  formatHistoryToolRow,
  formatLiveToolRow,
  formatToolGroupLabel,
  truncateSummary,
} from '@/lib/chat/tool-display';

export { liveActivityFromToolCall } from '@/lib/chat/tool-display';

type ActivityItem =
  | { kind: 'live'; activity: LiveToolActivity }
  | { kind: 'history'; message: Message };

type ChatToolActivityProps = {
  items: ActivityItem[];
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
};

function ActivityRow({ item }: { item: ActivityItem }) {
  const [expanded, setExpanded] = useState(false);
  const line = item.kind === 'live' ? formatLiveToolRow(item.activity) : formatHistoryToolRow(item.message);
  const detail = line.detail.trim();
  const showMore = detail.length > 240;
  const visibleDetail = showMore && !expanded ? truncateSummary(detail) : detail;
  const searchChips =
    item.kind === 'history' &&
    (item.message.toolName === 'search' || item.message.toolName === 'search_notes')
      ? parseSearchToolResultChips(item.message.content.result)
      : [];

  return createElement(
    'div',
    { className: 'chat-activity-row' },
    item.kind === 'live' && item.activity.status === 'running'
      ? createElement(ActivityIndicator, { size: 'small' })
      : createElement(
          'span',
          {
            className: `chat-activity-status${line.ok ? '' : ' chat-activity-status-error'}`,
            'aria-hidden': true,
          },
          line.ok ? '✓' : '✗',
        ),
    createElement(
      'div',
      { className: 'chat-activity-row-body' },
      createElement('div', { className: 'chat-activity-row-title' }, line.title),
      visibleDetail
        ? createElement(
            'div',
            { className: 'chat-activity-row-detail' },
            visibleDetail,
            showMore
              ? createElement(
                  Pressable,
                  { onPress: () => setExpanded((value) => !value) },
                  createElement(Text, { variant: 'caption' }, expanded ? ' Show less' : ' Show more'),
                )
              : null,
          )
        : null,
      searchChips.length > 0
        ? createElement(ChatSearchResultChips, { chips: searchChips })
        : null,
    ),
  );
}

function ActivityToggle({
  expanded,
  label,
  onPress,
}: {
  expanded: boolean;
  label: string;
  onPress: () => void;
}) {
  return createElement(
    'button',
    {
      type: 'button',
      className: 'chat-activity-toggle',
      onClick: onPress,
    },
    createElement(
      'span',
      { className: 'chat-activity-toggle-icon', 'aria-hidden': true },
      createElement(SidebarChevronIcon, { expanded, className: 'h-3 w-3' }),
    ),
    createElement('span', { className: 'chat-activity-toggle-label' }, label),
  );
}

export function ChatToolActivity({ items, collapsed, onToggleCollapsed }: ChatToolActivityProps) {
  if (items.length === 0) {
    return null;
  }

  const groupPreview = formatToolGroupLabel(items);

  if (collapsed && onToggleCollapsed) {
    return createElement(
      'div',
      { className: 'chat-activity-block' },
      createElement(ActivityToggle, {
        expanded: false,
        label: `Used ${items.length} ${items.length === 1 ? 'tool' : 'tools'} · ${groupPreview}`,
        onPress: onToggleCollapsed,
      }),
    );
  }

  return createElement(
    'div',
    { className: 'chat-activity-block' },
    onToggleCollapsed
      ? createElement(ActivityToggle, {
          expanded: true,
          label: `Activity · ${groupPreview}`,
          onPress: onToggleCollapsed,
        })
      : createElement('span', { className: 'chat-activity-toggle-label mb-1 block' }, 'Activity'),
    createElement(
      'div',
      { className: 'chat-activity-list' },
      ...items.map((item) =>
        createElement(ActivityRow, {
          key: item.kind === 'live' ? item.activity.id : item.message.id,
          item,
        }),
      ),
    ),
  );
}

